/**
 * useResolveAutoRefresh — react-to-SSE auto-refresh for readiness
 * hosts.
 *
 * The orchestrator emits ``readiness_resolution_applied`` over the
 * existing agent-session SSE stream whenever a background dispatch
 * (scoped materialize / per-edge regenerate / synthetic agent turn)
 * finishes. ``useWorkspaceSession`` re-broadcasts those events as a
 * window CustomEvent so any readiness host can subscribe without
 * opening a second EventSource.
 *
 * Usage in a host component:
 *
 *   ``useResolveAutoRefresh(sessionId, refreshReadiness)``
 *
 * The hook listens for the rebroadcast event, filters by ``sessionId``,
 * and calls the host's existing readiness-refresh function. When the
 * orchestrator reports ``ok=false`` the host still refreshes — the
 * failed dispatch leaves the finding in place and the operator can
 * pick a different resolution.
 */
import { useEffect } from 'react';

export interface ResolutionAppliedDetail {
  session_id: string;
  dispatch: 'materialize_mapping' | 'regenerate_edges' | 'agent_turn' | string;
  ok: boolean;
  affected_tables?: string[];
  edge_id?: string;
}

export function useResolveAutoRefresh(
  sessionId: string | undefined,
  refresh: () => void,
): void {
  useEffect(() => {
    if (!sessionId) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ResolutionAppliedDetail>).detail;
      if (!detail || detail.session_id !== sessionId) return;
      try {
        refresh();
      } catch {
        // Refresh failures are silent — the next user-driven trigger
        // (focus/visibility) will retry.
      }
    };
    window.addEventListener(
      'readiness:resolution-applied',
      handler as EventListener,
    );
    return () => {
      window.removeEventListener(
        'readiness:resolution-applied',
        handler as EventListener,
      );
    };
  }, [sessionId, refresh]);
}
